import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = 'https://hoosierlifeinsurance.com';

export default function LifeInsuranceForSeniorsIndiana() {
  useEffect(() => {
    document.title = 'Life Insurance for Seniors in Indiana — Best Options Over 60, 70 & 80';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Life insurance for Indiana seniors: best options over 60, 70, and 80. Compare term, whole life, and final expense policies. Guaranteed acceptance available.');

    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'life insurance for seniors Indiana, senior life insurance Indianapolis, life insurance over 60 Indiana, life insurance over 70 Indiana');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/resources/life-insurance-for-seniors-indiana`;

    const schemas = [
      {
        '@context': 'https://schema.org', '@type': 'Article',
        headline: 'Life Insurance for Seniors in Indiana: Best Options Over 60, 70 & 80',
        description: 'A complete guide to life insurance for Indiana seniors — comparing term, whole life, and final expense options at every age with real rate examples.',
        author: { '@type': 'Person', name: 'Benjamin Wright' },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-01-20T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/resources/life-insurance-for-seniors-indiana`,
      },
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Resources', item: `${SITE_URL}/resources` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance for Seniors in Indiana', item: `${SITE_URL}/resources/life-insurance-for-seniors-indiana` },
        ],
      },
      {
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: [
          { '@type': 'Question', name: 'Can Indiana seniors over 80 get life insurance?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Guaranteed issue final expense policies are available for Indiana seniors up to age 85. Coverage is limited to $25,000-$50,000 and there is a 2-year waiting period, but acceptance is guaranteed regardless of health. Some simplified issue policies are available up to age 80 with health questions but no medical exam.' } },
          { '@type': 'Question', name: 'Is term life insurance available for Indiana seniors over 70?', acceptedAnswer: { '@type': 'Answer', text: 'Term life insurance becomes very expensive and limited after age 70. Most carriers offer 10-year terms up to age 75-80. A 70-year-old in good health can get a 10-year $250,000 term policy for $200-$350/month. For most seniors over 70, whole life or final expense insurance is a better fit than term life.' } },
          { '@type': 'Question', name: 'What is the best life insurance for Indiana seniors on a fixed income?', acceptedAnswer: { '@type': 'Answer', text: 'For Indiana seniors on a fixed income, final expense insurance (burial insurance) is typically the best option. Coverage of $10,000-$25,000 costs $35-$150/month depending on age and health. Premiums are level for life and never increase. Simplified issue policies (no medical exam, just health questions) offer the best rates for seniors in average health.' } },
        ],
      },
    ];

    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-seniors-${i}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    return () => { schemas.forEach((_, i) => document.getElementById(`schema-seniors-${i}`)?.remove()); };
  }, []);

  const ageGroups = [
    {
      age: 'Ages 60-65',
      icon: 'ri-user-line',
      color: '#2D6A4F',
      bestOption: 'Term Life or Whole Life',
      why: 'Still young enough to qualify for competitive term rates. Healthy 60-year-olds can get 10-20 year term policies at reasonable rates. Whole life is also viable for legacy planning.',
      termRate: '$95-$145/mo',
      termCoverage: '$500K, 10-year term',
      finalExpenseRate: '$35-$50/mo',
      finalExpenseCoverage: '$10K final expense',
      topCarriers: ['Banner Life', 'Protective Life', 'Pacific Life'],
      keyConsiderations: [
        'Lock in rates now — they increase significantly each year',
        'Consider a 10-year term to bridge to Medicare/retirement',
        'Whole life builds cash value for retirement supplementation',
        'No-exam policies available up to $3M for healthy applicants',
      ],
    },
    {
      age: 'Ages 65-75',
      icon: 'ri-user-2-line',
      color: '#D97642',
      bestOption: 'Final Expense or Whole Life',
      why: 'Term life becomes expensive and limited. Most seniors in this range are best served by final expense insurance for burial costs, or a smaller whole life policy for legacy planning.',
      termRate: '$200-$450/mo',
      termCoverage: '$250K, 10-year term',
      finalExpenseRate: '$45-$115/mo',
      finalExpenseCoverage: '$15K final expense',
      topCarriers: ['Mutual of Omaha', 'Aetna', 'Foresters Financial'],
      keyConsiderations: [
        'Final expense insurance is the most affordable option',
        'No medical exam required for most final expense policies',
        'Premiums are locked in for life — never increase',
        'Guaranteed issue available if you have health conditions',
      ],
    },
    {
      age: 'Ages 75-85',
      icon: 'ri-user-3-line',
      color: '#1A3A52',
      bestOption: 'Final Expense (Guaranteed Issue)',
      why: 'At this age, final expense insurance is the primary option. Guaranteed issue policies accept everyone regardless of health. Coverage is limited but ensures your family won\'t face burial costs.',
      termRate: 'Not recommended',
      termCoverage: 'Very limited availability',
      finalExpenseRate: '$80-$260/mo',
      finalExpenseCoverage: '$15K final expense',
      topCarriers: ['Mutual of Omaha', 'AIG', 'Gerber Life'],
      keyConsiderations: [
        'Guaranteed issue = no health questions, no exam, no declines',
        '2-year waiting period before full death benefit pays',
        'Coverage limited to $25K-$50K maximum',
        'Apply as early as possible — rates increase with age',
      ],
    },
  ];

  return (
    <>
      <title>Life Insurance for Seniors in Indiana — Best Options Over 60, 70 &amp; 80 | Hoosier Life Insurance</title>
      <article className="min-h-screen bg-white">
        <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white py-20">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="mb-6">
              <Link to="/resources" className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm">
                <i className="ri-arrow-left-line mr-2" />Back to Resources
              </Link>
            </div>
            <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#D97642] text-sm font-semibold px-4 py-2 rounded-full mb-6">
              <i className="ri-user-3-line" />Senior Planning
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Life Insurance for Indiana Seniors Over 60, 70 &amp; 80 — Real Rates &amp; Options</h1>
            <p className="text-xl text-white/80 mb-8">The best options for Indiana seniors over 60, 70, and 80 — with real rates, carrier comparisons, and honest advice on what actually makes sense at each age.</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span><i className="ri-calendar-line mr-2" />Updated January 2026</span>
              <span><i className="ri-time-line mr-2" />10 min read</span>
              <span><i className="ri-map-pin-line mr-2" />Indiana Seniors Guide</span>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Life insurance for seniors is very different from coverage for younger adults. The options, costs, and strategies change significantly at 60, 70, and 80. This guide gives Indiana seniors an honest, age-specific breakdown of what's available and what actually makes sense.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The good news: <strong>Indiana seniors of any age can get life insurance coverage</strong> — even with serious health conditions. The key is knowing which type of policy fits your age, health, and goals.
            </p>
          </div>

          <div className="bg-[#FDF6F0] border-l-4 border-[#D97642] p-6 mb-12 rounded-r-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl" />Why Indiana Seniors Still Need Life Insurance
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: 'ri-building-2-line', reason: 'Cover funeral & burial costs ($8,000-$15,000)' },
                { icon: 'ri-file-text-line', reason: 'Pay off remaining debts (mortgage, credit cards)' },
                { icon: 'ri-heart-line', reason: 'Leave a legacy for children or grandchildren' },
                { icon: 'ri-user-heart-line', reason: 'Provide income for a surviving spouse' },
                { icon: 'ri-hospital-line', reason: 'Cover final medical bills and hospice costs' },
                { icon: 'ri-gift-line', reason: 'Fund a charitable donation or bequest' },
              ].map(r => (
                <div key={r.reason} className="flex items-start gap-2">
                  <i className={`${r.icon} text-[#D97642] mt-1 flex-shrink-0`} />
                  <span className="text-gray-800 text-sm">{r.reason}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Age Groups */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Best Life Insurance Options by Age Group</h2>
            <div className="space-y-8">
              {ageGroups.map(group => (
                <div key={group.age} className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden">
                  <div className="p-6 text-white" style={{ backgroundColor: group.color }}>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                        <i className={`${group.icon} text-3xl`} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">{group.age}</h3>
                        <p className="text-white/80">Best Option: <strong className="text-white">{group.bestOption}</strong></p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-700 mb-6">{group.why}</p>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Term Life Rate</p>
                        <p className="text-xl font-bold text-gray-900">{group.termRate}</p>
                        <p className="text-xs text-gray-500">{group.termCoverage}</p>
                      </div>
                      <div className="bg-[#FDF6F0] rounded-xl p-4">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Final Expense Rate</p>
                        <p className="text-xl font-bold text-[#D97642]">{group.finalExpenseRate}</p>
                        <p className="text-xs text-gray-500">{group.finalExpenseCoverage}</p>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Top Carriers for This Age Group</p>
                        <div className="space-y-2">
                          {group.topCarriers.map(c => (
                            <div key={c} className="flex items-center gap-2 text-sm text-gray-700">
                              <i className="ri-checkbox-circle-fill text-[#D97642]" />{c}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Key Considerations</p>
                        <div className="space-y-2">
                          {group.keyConsiderations.map(c => (
                            <div key={c} className="flex items-start gap-2 text-sm text-gray-700">
                              <i className="ri-arrow-right-s-line text-[#D97642] mt-1 flex-shrink-0" />{c}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rate Table */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Indiana Senior Life Insurance Rate Table</h2>
            <p className="text-gray-700 mb-6">Monthly rates for Indiana seniors (non-smoker, good health, simplified issue where applicable):</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-5 py-4 text-left font-bold">Age</th>
                    <th className="px-5 py-4 text-left font-bold">$10K Final Expense</th>
                    <th className="px-5 py-4 text-left font-bold">$25K Final Expense</th>
                    <th className="px-5 py-4 text-left font-bold">$100K Whole Life</th>
                    <th className="px-5 py-4 text-left font-bold">$250K Term (10yr)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    { age: 60, fe10: '$35', fe25: '$80', wl: '$185', term: '$95' },
                    { age: 65, fe10: '$45', fe25: '$105', wl: '$240', term: '$145' },
                    { age: 70, fe10: '$60', fe25: '$135', wl: '$320', term: '$250' },
                    { age: 75, fe10: '$80', fe25: '$185', wl: '$450', term: '$420' },
                    { age: 80, fe10: '$110', fe25: '$260', wl: 'N/A', term: 'N/A' },
                    { age: 85, fe10: '$155*', fe25: '$360*', wl: 'N/A', term: 'N/A' },
                  ].map(row => (
                    <tr key={row.age} className="hover:bg-[#F9F7F4]">
                      <td className="px-5 py-4 font-bold text-gray-900">Age {row.age}</td>
                      <td className="px-5 py-4 text-[#D97642] font-bold">{row.fe10}/mo</td>
                      <td className="px-5 py-4 text-[#D97642] font-bold">{row.fe25}/mo</td>
                      <td className="px-5 py-4 text-gray-700">{row.wl !== 'N/A' ? `${row.wl}/mo` : <span className="text-gray-400">Not available</span>}</td>
                      <td className="px-5 py-4 text-gray-700">{row.term !== 'N/A' ? `${row.term}/mo` : <span className="text-gray-400">Not available</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-500 mt-3">*Age 85 rates are guaranteed issue (2-year waiting period). All other rates are simplified issue. Smokers pay 30-50% more.</p>
          </div>

          {/* FAQ */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Common Questions from Indiana Seniors</h2>
            <div className="space-y-4">
              {[
                { q: 'Can Indiana seniors over 80 get life insurance?', a: 'Yes. Guaranteed issue final expense policies are available up to age 85. Coverage is limited to $25K-$50K with a 2-year waiting period, but acceptance is guaranteed regardless of health.' },
                { q: 'Is term life insurance available for Indiana seniors over 70?', a: 'Term life becomes very expensive after 70. Most carriers offer 10-year terms up to age 75-80. For most seniors over 70, final expense or whole life insurance is a better fit.' },
                { q: 'What is the best life insurance for Indiana seniors on a fixed income?', a: 'Final expense insurance is typically the best option. Coverage of $10K-$25K costs $35-$150/month depending on age and health. Premiums are level for life and never increase.' },
              ].map(faq => (
                <div key={faq.q} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-question-line text-[#D97642] mr-3 text-2xl" />{faq.q}
                  </h3>
                  <p className="text-gray-700 text-sm pl-11">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-4">Find the Right Coverage for Your Age and Health</h2>
            <p className="text-xl text-white/80 mb-8">Benjamin Wright specializes in senior life insurance for Indiana residents. Let's find the most affordable option for your situation.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/senior-life-insurance-indianapolis" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-50 transition-colors whitespace-nowrap">See Senior Rates</Link>
              <Link to="/book-appointment" className="bg-[#0F2A3F] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap">Talk to a Specialist</Link>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { to: '/resources/final-expense-insurance-indiana-seniors', icon: 'ri-hand-heart-line', title: 'Final Expense Insurance for Indiana Seniors', desc: 'Complete guide to burial insurance for Indiana seniors' },
                { to: '/life-insurance-for-seniors-over-60-indianapolis', icon: 'ri-map-pin-line', title: 'Senior Life Insurance Indianapolis', desc: 'Indianapolis-specific rates and options for seniors over 60' },
                { to: '/guaranteed-issue-life-insurance-indianapolis', icon: 'ri-shield-check-line', title: 'Guaranteed Issue Life Insurance', desc: 'No health questions — guaranteed acceptance for Indiana seniors' },
              ].map(r => (
                <Link key={r.to} to={r.to} className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                  <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className={`${r.icon} text-2xl`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">{r.title}</h3>
                  <p className="text-sm text-gray-600">{r.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions About Senior Life Insurance in Indiana?</h2>
            <p className="text-xl text-gray-700 mb-8">Our Indiana-based agents specialize in senior coverage. Let's find the right policy for your age, health, and budget.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3176482983" className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#0F2A3F] transition-colors whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-phone-line mr-2" />Call (317) 648-2983
              </a>
              <Link to="/contact" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-message-3-line mr-2" />Send a Message
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
